import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import toast from 'react-hot-toast';
import { useClient } from 'jsonapi-react';
import { FiCalendar, FiCheck, FiChevronDown } from 'react-icons/fi';
import { Badge } from '@/components/ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { ModalWarning } from '@/components/custom/modal/modal-warning';
import { PlayfulCardButton } from '@/components/custom/playful-card';
import usePermission from '@/hooks/usePermission';
import { statusBadgeText } from '../utils/BadgeEssay';

// Mesma paleta soft usada no card lúdico do aluno (Playful/EssayCard.jsx) e no
// card de simulado (PlayfulMockExamsCard) pro estado "Prazo encerrado" — duplicado
// de propósito: cada tema/papel mantém seu próprio mapeamento visual.
const STATUS_BADGE_VARIANT = {
  accepting_proposal: 'info-soft',
  processing: 'amber-soft',
  reviewing: 'amber-soft',
  ocr_review: 'amber-soft',
  sending_by_teacher: 'amber-soft',
  unreviewed: 'success-soft',
  reviewed: 'success-soft',
  annulled: 'danger-soft',
  annulled_by_teacher: 'danger-soft',
  minimum_length_not_satisfied: 'danger-soft',
  failed_to_process: 'danger-soft',
  close_for_submissions: 'muted',
  not_sent: 'muted'
};

export function EssayRow({ essay, index = 0, deleteEssay }) {
  const navigate = useNavigate();
  const client = useClient();

  const [unpublishWarning, setUnpublishWarning] = useState(false);
  const [publishWarning, setPublishWarning] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Todas as permissões resolvidas incondicionalmente aqui em cima — hooks não
  // podem ser chamados dentro de JSX condicional (mesmo ajuste feito em
  // course-admin-card.jsx pro menu de ações do curso).
  const canEdit = usePermission('Editar redação', 'Redação');
  const canDelete = usePermission('Excluir redação', 'Redação');
  const showActionsMenu = canEdit || canDelete;

  const accepting_proposal = essay?.status === 'accepting_proposal';
  const reviewing = essay?.status === 'reviewing';
  const grade = essay?.['essay-student']?.grade ?? 0;

  let badgeVariant = STATUS_BADGE_VARIANT[essay?.status];
  let badgeText = statusBadgeText[essay?.status];

  if (accepting_proposal && moment(essay?.['publish-at']).isAfter(moment())) {
    badgeText = 'Agendada';
    badgeVariant = 'amber-soft';
  } else if (reviewing) {
    badgeText = statusBadgeText.unreviewed;
    badgeVariant = 'success-soft';
  }

  const submissionDeadline = `Prazo de envio: ${moment(essay?.['publish-at']).format('DD/MM/YYYY')} às ${moment(essay?.['publish-at']).format('HH:mm')} à ${moment(essay?.['delivery-date']).format('DD/MM/YYYY')}`;

  const meta = [{ icon: FiCalendar, text: submissionDeadline }];

  if (['processing', 'annulled', 'annulled_by_teacher'].includes(essay?.status)) {
    meta.push({ text: `${essay?.['essay-student']?.['submission_order']}° envio` });
  } else if (['reviewed', 'unreviewed', 'reviewing'].includes(essay?.status)) {
    meta.push({ icon: FiCheck, text: `Pontuação ${grade}` });
    meta.push({ text: `${essay?.['essay-student']?.['submission_order']}° envio` });
  }

  const updateEssayPublicity = async () => {
    setIsSubmitting(true);
    const { error } = await client.mutate(`essays/${essay.id}/toggle_visibility`, {}, { method: 'PATCH' });
    if (error) {
      toast.error('Erro ao atualizar redação');
    } else {
      toast.success(essay.visible ? 'Despublicado com sucesso!' : 'Publicado com sucesso!');
      essay.visible = !essay.visible;
    }
    setIsSubmitting(false);
    setUnpublishWarning(false);
    setPublishWarning(false);
  };

  const handlePublishToggle = () => {
    if (essay.visible) {
      setPublishWarning(true);
    } else {
      setUnpublishWarning(true);
    }
  };

  return (
    <>
      <div
        className="flex flex-wrap items-center gap-4 rounded-[16px] border border-neutral-100 bg-white p-4 shadow-card transition-shadow duration-150 hover:shadow-card-hover"
        style={{ animation: `trilha-in 380ms cubic-bezier(0.23, 1, 0.32, 1) ${250 + index * 70}ms both` }}
      >
        <div className="min-w-[200px] flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3
              className="mb-0 line-clamp-2 text-base font-extrabold leading-snug"
              style={{ color: '#222731' }}
              title={essay?.topic}
            >
              {essay?.topic}
            </h3>

            {badgeText && (
              <Badge
                variant={badgeVariant}
                className="shrink-0 rounded-full px-2.5 py-1 text-xs font-bold normal-case"
              >
                {badgeText}
              </Badge>
            )}

            <Badge
              variant={essay.visible ? 'success-soft' : 'muted'}
              className="shrink-0 rounded-full px-2.5 py-1 text-xs font-bold normal-case"
            >
              {essay.visible ? 'Publicado' : 'Não publicado'}
            </Badge>
          </div>

          {meta.length > 0 && (
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs font-semibold text-neutral-500">
              {meta.map((item, metaIndex) => (
                <span
                  key={metaIndex}
                  className="flex items-center gap-1.5"
                >
                  {item.icon && <item.icon className="h-3.5 w-3.5 text-mission" />}
                  {item.text}
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <PlayfulCardButton
            className="h-9 w-auto px-6"
            onClick={() => navigate(`/redacao/acompanhamento/${essay?.id}`)}
          >
            Acessar
          </PlayfulCardButton>

          {showActionsMenu && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <PlayfulCardButton
                  variant="outline"
                  aria-label="Opções"
                  className="h-9 w-9 shrink-0 gap-0 p-0"
                >
                  <FiChevronDown size={20} />
                </PlayfulCardButton>
              </DropdownMenuTrigger>

              <DropdownMenuContent align="end">
                {canEdit && <DropdownMenuItem onSelect={handlePublishToggle}>{essay.visible ? 'Despublicar' : 'Publicar'}</DropdownMenuItem>}
                {canEdit && <DropdownMenuItem onSelect={() => navigate(`/redacao/editar/curso/${essay?.id}`)}>Editar</DropdownMenuItem>}
                {canDelete && <DropdownMenuItem onSelect={() => deleteEssay(essay?.id)}>Excluir</DropdownMenuItem>}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      <ModalWarning
        isSubmitting={isSubmitting}
        show={unpublishWarning}
        titleFirstButton="Sim. Quero publicar"
        titleSecondButton="Não. Manter despublicada"
        onClose={() => setUnpublishWarning(false)}
        title="Você tem certeza que deseja publicar esta redação?"
        description="Ao publicar, esta redação ficará visível para os alunos."
        onSubmitFirstButton={updateEssayPublicity}
        onSubmitSecondButton={() => setUnpublishWarning(false)}
      />

      <ModalWarning
        isSubmitting={isSubmitting}
        show={publishWarning}
        titleFirstButton="Sim. Quero despublicar"
        titleSecondButton="Não. Manter publicada"
        onClose={() => setPublishWarning(false)}
        title="Você tem certeza que deseja despublicar esta redação?"
        description="Ao despublicar, esta redação ficará invisível para os alunos."
        onSubmitFirstButton={updateEssayPublicity}
        onSubmitSecondButton={() => setPublishWarning(false)}
      />
    </>
  );
}
